import i18n from '@/i18n'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { uiApi } from '../lib/api/uiApi'
import { useLanguageStore } from '../stores/useLanguageStore'
import type { LocalizedSelectOption, SelectOption } from '../types/ui'
import {
  getEnumLabel as getLocalizedEnumLabel,
  getEnumOptionLabel as getLocalizedEnumOptionLabel,
} from '../utils/enum-label'

const localizeOptions = (
  options: SelectOption[] | undefined,
  getEnumOptionLabel: (option: SelectOption | undefined) => string | undefined,
): LocalizedSelectOption[] | undefined => {
  return options?.map(option => ({
    ...option,
    label: getEnumOptionLabel(option) ?? option.value,
  }))
}

export const useUI = () => {
  const { data } = useQuery({ queryKey: ['ui'], queryFn: uiApi.getUI, staleTime: Infinity })
  const language = useLanguageStore(state => state.language)
  const setLanguage = useLanguageStore(state => state.setLanguage)
  const setAppLanguage = (lang: 'th' | 'en') => {
    if (lang === language) return

    setLanguage(lang)
    i18n.changeLanguage(lang)
  }

  const getEnumOptionLabel = (option: SelectOption | undefined) => getLocalizedEnumOptionLabel(option, language)
  const getEnumLabel = (options: SelectOption[] | undefined, value: string | null | undefined, fallback?: string) =>
    getLocalizedEnumLabel(options, value, language, fallback)

  const localized = useMemo(
    () => ({
      userRoles: localizeOptions(data?.userRoles, getEnumOptionLabel),
      itemStatuses: localizeOptions(data?.itemStatuses, getEnumOptionLabel),
      manualItemStatuses: localizeOptions(data?.manualItemStatuses, getEnumOptionLabel),
      repairStatuses: localizeOptions(data?.repairStatuses, getEnumOptionLabel),
      repairPriorities: localizeOptions(data?.repairPriorities, getEnumOptionLabel),
      transferStatuses: localizeOptions(data?.transferStatuses, getEnumOptionLabel),
      activeTransferStatuses: localizeOptions(data?.activeTransferStatuses, getEnumOptionLabel),
      productCategories: localizeOptions(data?.productCategories, getEnumOptionLabel),
      productTypes: localizeOptions(data?.productTypes, getEnumOptionLabel),
      gatewayTypes: localizeOptions(data?.gatewayTypes, getEnumOptionLabel),
      deliveryMethods: localizeOptions(data?.deliveryMethods, getEnumOptionLabel),
      quotationStatuses: localizeOptions(data?.quotationStatuses, getEnumOptionLabel),
      logActions: localizeOptions(data?.logActions, getEnumOptionLabel),
    }),
    [data, language],
  )

  return {
    ...localized,
    language,
    setLanguage: setAppLanguage,
    getEnumLabel,
    getEnumOptionLabel,
  }
}

export type { EnumLabelLanguage } from '../utils/enum-label'
